import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';

import { ContentLayout } from '@/components/layouts';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import { fetchMatchData } from '@/features/match/api/getMatchData';
import { fetchMatchStatus } from '@/features/match/api/getMatchStatus';
import Banner from '@/features/match/components/Banner';
import MapAndMvp from '@/features/match/components/MapAndMvp';
import MatchTabs from '@/features/match/components/match-tabs/MatchTabs';
import Scoreboard from '@/features/match/components/Scoreboard';
import Title from '@/features/match/components/Title';
import { MatchStatus } from '@/features/match/types';

const MatchRoute = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const {
    data: matchData,
    error,
    isFetched,
  } = useQuery({
    queryKey: ['matchData', id],
    queryFn: () => fetchMatchData(id, true),
    retry: 0,
    enabled: !!id,
  });
  const { data: matchStatus } = useQuery({
    queryKey: ['matchStatus', id],
    queryFn: () => fetchMatchStatus(id),
    enabled: !!id,
  });

  if (error) {
    if (error.message === 'Match not found') {
      toast({
        description: 'Match not found',
        variant: 'destructive',
      });
      navigate('/');
    } else {
      console.error('An error occurred:', error.message);
    }
  }

  if (!id || !/^\d+$/.test(id)) {
    toast({
      description: 'Invalid match ID',
      variant: 'destructive',
      itemID: 'invalid-id',
    });
    navigate('/');
    return <Spinner size="lg" className="mx-auto" />;
  }

  if (isFetched && (!matchData?.team1 || !matchData?.team2)) {
    toast({
      description: 'This match is not available',
      variant: 'destructive',
    });
    navigate('/');
    return null;
  }

  return (
    <ContentLayout
      title={`${matchData?.team1?.team_name} VS ${matchData?.team2?.team_name}`}
      description="A league match for the current running SCL Season, played in Counter-Strike 2."
    >
      <div className="mb-3">
        <Title match={matchData} />
        <Banner match={matchData} status={matchStatus} />
        {matchData && matchStatus === MatchStatus.ENDED && (
          <div className="mt-8 flex flex-col gap-y-8">
            <>
              {matchData.playoffs ? (
                <>{matchData && <MatchTabs match={matchData} matchId={id} />}</>
              ) : (
                <>
                  {matchData.get5StatsMatch.get5StatsMaps.length > 0 && (
                    <>
                      <MapAndMvp match={matchData} mapNumber={0} />
                      <Scoreboard
                        matchId={id}
                        mapNumber={0}
                        match={matchData}
                      />
                    </>
                  )}
                </>
              )}
            </>
          </div>
        )}
        {matchData && matchStatus && matchStatus !== MatchStatus.ENDED && (
          <div className="mt-8 text-center text-lg font-semibold">
            Match has been cancelled
          </div>
        )}
      </div>
    </ContentLayout>
  );
};

export default MatchRoute;
